import { useMemo, type ReactNode } from "react";
import {
  ListContextProvider,
  type RaRecord,
  useGetList,
  useList,
  type FieldProps,
} from "react-admin";

import { useTypedFieldValue } from "@/hooks";
import { type ModuleValue } from "@/types/modules";

export type Props<T extends RaRecord> = Omit<FieldProps<T>, "resource"> & {
  idField?: keyof T;
  module: ModuleValue;
  children: ReactNode;
};

const STALE_TIME = 60_000;

export const ReferenceTextArrayField = <T extends RaRecord>(
  props: Props<T>,
) => {
  const { children, module, idField = "id" } = props;
  const { data = [], isPending } = useGetList<T>(module, undefined, {
    staleTime: STALE_TIME,
  });
  const fieldValue = useTypedFieldValue<string[], Record<string, unknown>>(
    props,
  );

  const recordMap = useMemo(
    () =>
      data.reduce<Record<string, T>>((map, record) => {
        const key = String(record[idField]);
        map[key] = record;

        return map;
      }, {}),
    [data, idField],
  );
  const enrichedFieldValue = useMemo(
    () =>
      isPending
        ? []
        : (fieldValue ?? []).map(
            (value: string) =>
              recordMap[value] ??
              ({
                id: value,
                [idField]: value,
                name: value,
                title: value,
              } as unknown as T),
          ),
    [fieldValue, idField, isPending, recordMap],
  );
  const listContext = useList<T>({
    data: enrichedFieldValue,
    resource: module,
    isPending,
  });

  return (
    <ListContextProvider value={listContext}>{children}</ListContextProvider>
  );
};
ReferenceTextArrayField.defaultProps = { addLabel: true };
